import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Link,
    List,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CustomButton from 'components/UI/buttons/CustomButton.style';
import styles from 'features/DefaultHelp.module.css';
import YardCheckStatusChip from 'features/yardCheck/components/YardCheckStatusChip/YardCheckStatusChip';
import { useCallback, useMemo } from 'react';
import { YARD_CHECK_ITEM_TYPE } from 'utils/constants';
const { MISSING, NEW, MATCHED, UPDATED } = YARD_CHECK_ITEM_TYPE;

const ReviewContent = () => {
    const instructions = useMemo(
        () => [
            {
                key: 'remediateItems',
                title: 'Remediate Yard Check Items',
            },
        ],
        []
    );

    const renderOverviewSection = useCallback(
        () => (
            <>
                <h4>OVERVIEW</h4>
                <Typography>
                    Once a Yard Check has progressed to the <b>Review</b> state, users can finalize the verification of assets by assigning a{' '}
                    <b>remediation action</b> to each item based on its status. These actions determine how each asset will be updated (or not) in the{' '}
                    <b>Current Inventory</b>.
                </Typography>
            </>
        ),
        []
    );

    const renderControlsSection = useCallback(
        () => (
            <>
                <h4>CONTROLS</h4>
                <Table className={styles['controls-table']}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Actions</TableCell>
                            <TableCell>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Yard Check Filters</TableCell>
                            <TableCell>Toggle the visibility of asset types by status: Missed, Added, Matched, or Updated.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="success" disabled>
                                    Completed
                                </CustomButton>
                            </TableCell>
                            <TableCell>
                                Submit all changes and finalize the Yard Check. Changes are pushed to <b>Inventory</b>.
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                <CustomButton customVariant="error">Cancel</CustomButton>
                            </TableCell>
                            <TableCell>
                                Cancel the review process and return to the previous stage (<b>Asset Entry</b>).
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        ),
        []
    );

    const renderActionGuideLineSection = useCallback(
        () => (
            <>
                <h4>ACTION GUIDELINE</h4>
                <Table className={styles['controls-table']}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>Available Actions</TableCell>
                            <TableCell>Action Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Missed */}
                        <TableRow>
                            <TableCell rowSpan={2}>
                                <YardCheckStatusChip status={MISSING} />
                            </TableCell>
                            <TableCell>Remove</TableCell>
                            <TableCell>Asset will be removed from Current Inventory.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Ignore</TableCell>
                            <TableCell>No change will be made; asset remains in Inventory.</TableCell>
                        </TableRow>

                        {/* New */}
                        <TableRow>
                            <TableCell rowSpan={2}>
                                <YardCheckStatusChip status={NEW} />
                            </TableCell>
                            <TableCell>Add</TableCell>
                            <TableCell>Asset will be added as new entry in Current Inventory.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Ignore</TableCell>
                            <TableCell>No change will be made.</TableCell>
                        </TableRow>

                        {/* Updated */}
                        <TableRow>
                            <TableCell rowSpan={2}>
                                <YardCheckStatusChip status={UPDATED} />
                            </TableCell>
                            <TableCell>Update</TableCell>
                            <TableCell>Updates the asset’s information in Inventory based on changes.</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Ignore</TableCell>
                            <TableCell>No update will be applied.</TableCell>
                        </TableRow>

                        {/* Matched */}
                        <TableRow>
                            <TableCell>
                                <YardCheckStatusChip status={MATCHED} />
                            </TableCell>
                            <TableCell>No action needed</TableCell>
                            <TableCell>Asset matches both physically and digitally — nothing to change.</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </>
        ),
        []
    );

    const renderInstructionContent = useCallback(
        () => (
            <Box className={styles['instruction-child-container']}>
                <List className={styles['unordered-list']}>
                    <ListItemText>
                        1. Review each item listed in the <b>Yard Check Summary</b> table.
                    </ListItemText>
                    <ListItemText>
                        2. Based on the <b>status</b> (Missed, Added, Matched, or Updated), choose the appropriate remediation
                        <b> action</b> from the Options column.
                    </ListItemText>
                    <ListItemText>3. Use filters to focus on specific asset types for faster review.</ListItemText>
                    <ListItemText>
                        4. Click <b>Complete</b> to confirm and push changes to the system’s Current Inventory.
                    </ListItemText>
                </List>
            </Box>
        ),
        []
    );

    const renderInstructionsSection = useCallback(
        () => (
            <>
                <h4>INSTRUCTIONS</h4>
                <Box className={styles['instructions-accordion-container']}>
                    {instructions.map((instruction, index) => (
                        <Accordion key={instruction.key} defaultExpanded={index === 0} disableGutters className={styles['instruction-accordion']}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`${instruction.key}-content`}
                                id={`${instruction.key}-header`}
                                className={styles['instruction-accordion-summary']}
                            >
                                <Typography className={styles['instruction-header']}>{instruction.title}</Typography>
                            </AccordionSummary>
                            <AccordionDetails className={styles['instruction-accordion-details']}>{renderInstructionContent()}</AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </>
        ),
        [instructions, renderInstructionContent]
    );

    const renderDivider = useCallback(() => <Divider className={styles['divider']} />, []);

    const renderFooter = useCallback(
        () => (
            <Stack className={styles['footer']}>
                <span>
                    {`For further support, email us at `}
                    <Link href="mailto:cs@birdseye.ca" underline="none">
                        cs@birdseye.ca
                    </Link>
                    {'.'}
                </span>
            </Stack>
        ),
        []
    );

    return (
        <Stack className={styles['help-dialog-container']}>
            <Box className={styles['help-dialog-content-container']}>
                {renderOverviewSection()}
                {renderDivider()}
                {renderControlsSection()}
                {renderDivider()}
                {renderActionGuideLineSection()}
                {renderDivider()}
                {renderInstructionsSection()}
            </Box>
            {renderFooter()}
        </Stack>
    );
};

export default ReviewContent;
